import { createFileRoute } from "@tanstack/react-router";
import { galleryItems, type GalleryItem } from "@/content/gallery-items";

const eventLogos = import.meta.glob("../assets/events/*", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

const groups: { id: GalleryItem["group"]; title: string; note?: string }[] = [
  { id: "used", title: "Zdjęcia Sylwii, użyte na stronie" },
  { id: "unused", title: "Zdjęcia Sylwii, na razie nieużyte", note: "Można je wykorzystać w nowych sekcjach lub artykułach." },
  { id: "people", title: "Zdjęcia osób z rekomendacji i opinii" },
  { id: "books", title: "Okładki książek" },
  { id: "logos", title: "Logotypy mediów i wydarzeń" },
];

function srcFor(item: GalleryItem): string | undefined {
  if (item.url) return item.url;
  if (item.path) {
    const key = `../${item.path.replace(/^src\//, "")}`;
    return eventLogos[key];
  }
  return undefined;
}

// Prywatna strona podglądu: otwiera się wyłącznie z kluczem w adresie,
// np. /galeria-zdjec?podglad=GALERIA_TOKEN. Bez klucza — komunikat o braku dostępu.
const GALLERY_TOKEN = "sylwia-podglad-x7q2m9";

export const Route = createFileRoute("/galeria-zdjec")({
  validateSearch: (search: Record<string, unknown>) => ({
    podglad: typeof search["podglad"] === "string" ? search["podglad"] : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Galeria zdjęć, podgląd wewnętrzny" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: GalleryPage,
});

function GalleryPage() {
  const { podglad } = Route.useSearch();
  if (podglad !== GALLERY_TOKEN) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4">
        <title>Nie znaleziono strony | Sylwia Sitkowska</title>
        <meta name="robots" content="noindex, follow" />
        <div className="max-w-md text-center">
          <h1 className="text-7xl font-bold text-foreground">404</h1>
          <h2 className="mt-4 text-xl font-semibold text-foreground">Nie znaleziono strony</h2>
        </div>
      </main>
    );
  }
  return (
    <main className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <h1 className="font-serif text-3xl sm:text-4xl">Wszystkie grafiki w projekcie</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">
        Strona prywatna, niewidoczna w menu i dla Google. Zdjęcia pogrupowane: użyte na stronie,
        nieużyte, zdjęcia osób, okładki książek i logotypy.
      </p>
      {groups.map((group) => {
        const items = galleryItems.filter((i) => i.group === group.id);
        if (items.length === 0) return null;
        return (
          <section key={group.id} className="mt-12">
            <h2 className="font-serif text-2xl">{group.title}</h2>
            {group.note ? <p className="mt-1 text-sm text-muted-foreground">{group.note}</p> : null}
            <ul className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((item) => {
                const src = srcFor(item);
                return (
                  <li key={item.name} className="overflow-hidden rounded-lg border border-border bg-card">
                    {src ? (
                      <img src={src} alt={item.name} loading="lazy" className="aspect-[4/3] w-full object-contain bg-muted" />
                    ) : (
                      <div className="flex aspect-[4/3] items-center justify-center bg-muted text-xs text-muted-foreground">
                        podgląd niedostępny
                      </div>
                    )}
                    <p className="px-3 py-2 text-xs text-muted-foreground break-all">
                      {item.name}
                      {item.group === "unused" ? (
                        <span className="ml-1 rounded bg-accent px-1.5 py-0.5 text-[10px] font-medium text-accent-foreground">
                          nieużyte
                        </span>
                      ) : null}
                    </p>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </main>
  );
}
